// Pääskriptin orkestrointi: kytkee blokkauksen, GCM:n, tallennuksen,
// API-kutsut ja käyttöliittymän yhteen. Vastaa vaihe 0:n cmp-script-edge-
// funktion palauttamaa IIFE:ä käyttäytymiseltään identtisesti.

import type { BlockedEntry, BlockPatterns, Choices, CmpConfig } from './types';
import { getStoredConsent as storageGetStoredConsent, getConsentId, storeConsent as storageStoreConsent, consentKey, gcmKey, patternsKey } from './storage';
import { MAIN_FALLBACK_PATTERNS, shouldBlockSrc } from './patterns';
import { fetchBannerConfig, sendConsent as apiSendConsent } from './api-client';
import { renderBanner, showConfirmation, type BannerUiContext } from './ui';

export interface CmpInitOptions {
  domain: string;
  apiBase: string;
}

/**
 * Käynnistää CMP:n: aloittaa blokkauksen heti (jos inline-blokkeri ei ole jo
 * tehnyt sitä), hakee konfiguraation ja joko soveltaa tallennetun
 * suostumuksen tai näyttää bannerin.
 */
export function initCmp(options: CmpInitOptions): void {
  const domain = options.domain;
  const API_BASE = options.apiBase;
  const STORAGE_KEY = consentKey(domain);
  let CONFIG: CmpConfig | null = null;
  const LANG = document.documentElement.lang || navigator.language?.substring(0, 2) || 'fi';

  // ===== SCRIPT BLOCKING =====
  // Inline-blokkeri on ensisijainen; pääskriptin oma blokkaus on fallback
  // sivuille, joilta inline-blokkeri puuttuu.
  const _hasInlineBlocker = !!window.__cmpBlocking;

  const _blockedScripts: BlockedEntry[] = _hasInlineBlocker ? window.__cmpBlocking!.blocked : [];
  const _blockPatterns: BlockPatterns = _hasInlineBlocker ? window.__cmpBlocking!.patterns : {};
  let _observer: MutationObserver | null = _hasInlineBlocker ? window.__cmpBlocking!.observer : null;
  let _consentApplied = false;
  let _currentConsent: Choices | null = _hasInlineBlocker ? window.__cmpBlocking!.consent : null;
  const _origCreate = _hasInlineBlocker ? window.__cmpBlocking!.origCreate : document.createElement.bind(document);

  function initBlockPatterns() {
    if (_hasInlineBlocker) return; // Inline-blokkeri alusti jo patternit
    for (const slug in MAIN_FALLBACK_PATTERNS) {
      _blockPatterns[slug] = MAIN_FALLBACK_PATTERNS[slug].slice();
    }
  }

  function refineBlockPatterns(config: CmpConfig) {
    const cats = config.categories || [];
    cats.forEach(function (cat) {
      if (cat.is_required) return;
      if (!_blockPatterns[cat.slug]) _blockPatterns[cat.slug] = [];
      (cat.cookies || []).forEach(function (ck) {
        if (ck.provider && _blockPatterns[cat.slug].indexOf(ck.provider) === -1) {
          _blockPatterns[cat.slug].push(ck.provider);
        }
      });
    });
  }

  function shouldBlock(src: string | null | undefined, consent: Choices | null | undefined) {
    if (_hasInlineBlocker) return window.__cmpBlocking!.shouldBlock(src, consent);
    return shouldBlockSrc(_blockPatterns, src, consent);
  }

  if (!_hasInlineBlocker) {
    // === createElement-override (ensisijainen fallback-esto) ===
    document.createElement = function (tag: string) {
      const el = _origCreate(tag);
      if (tag.toLowerCase() === 'script') {
        let _realSrc = '';
        Object.defineProperty(el, 'src', {
          get: function () { return _realSrc; },
          set: function (val: string) {
            const match = shouldBlock(val, _currentConsent);
            if (match) {
              (el as HTMLScriptElement).type = 'text/plain';
              el.setAttribute('data-cmp-blocked', match.slug);
              el.setAttribute('data-cmp-original-src', val);
              _blockedScripts.push({ node: el, slug: match.slug, src: val });
              _realSrc = '';
              console.log('CMP: createElement blocked [' + match.slug + ']', val);
              return;
            }
            _realSrc = val;
            el.setAttribute('src', val);
          },
          configurable: true,
        });
      }
      return el;
    } as typeof document.createElement;
  }

  function startBlocking(consent: Choices | null) {
    if (_hasInlineBlocker) return; // Inline-blokkeri hoitaa tämän
    if (_observer) return;
    _observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType === 1 && (node as Element).tagName === 'SCRIPT') {
            const el = node as HTMLScriptElement;
            const src = el.src || el.getAttribute('src');
            const match = shouldBlock(src, consent);
            if (match) {
              el.type = 'text/plain';
              el.setAttribute('data-cmp-blocked', match.slug);
              el.setAttribute('data-cmp-original-src', src as string);
              _blockedScripts.push({ node: el, slug: match.slug, src: src as string });
              console.log('CMP: blocked script [' + match.slug + ']', src);
            }
          }
        });
      });
    });
    _observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  // Staattisesti portitetut tagit: <script type="text/plain" data-cmp-category="...">
  // aktivoidaan, kun kävijä hyväksyy kyseisen kategorian.
  function activateStaticScripts(choices: Choices) {
    const staticBlocked = document.querySelectorAll('script[type="text/plain"][data-cmp-category]');
    staticBlocked.forEach(function (el) {
      const cat = el.getAttribute('data-cmp-category') as string;
      if (choices[cat]) {
        const ns = _origCreate('script');
        const src = el.getAttribute('data-cmp-src') || el.getAttribute('src');
        if (src) ns.src = src;
        else ns.textContent = el.textContent;
        const attrs = el.attributes;
        for (let j = 0; j < attrs.length; j++) {
          const nm = attrs[j].name;
          if (nm === 'type' || nm === 'src' || nm.indexOf('data-cmp-') === 0) continue;
          ns.setAttribute(nm, attrs[j].value);
        }
        if (el.parentNode) el.parentNode.insertBefore(ns, el.nextSibling);
        el.remove();
        console.log('CMP: activated static script [' + cat + ']', src);
      }
    });
  }

  function applyConsent(choices: Choices) {
    _consentApplied = true;
    _currentConsent = choices;

    // Google Consent Mode v2: lähetä update-signaali
    try {
      if (window.__cmpGcm && window.__cmpGcm.enabled && typeof window.__cmpGcm.update === 'function') {
        window.__cmpGcm.update(choices);
      }
    } catch (e) { console.warn('CMP: gcm update failed', e); }

    // Staattiset skriptit aktivoidaan aina, oli inline-blokkeri tai ei
    activateStaticScripts(choices);

    if (_hasInlineBlocker) {
      // Dynaamisten skriptien vapautus delegoidaan inline-blokkerille
      window.__cmpBlocking!.apply(choices);
      return;
    }

    // Vapauta hyväksyttyjen kategorioiden skriptit
    const toUnblock: BlockedEntry[] = [];
    for (let i = _blockedScripts.length - 1; i >= 0; i--) {
      const entry = _blockedScripts[i];
      if (choices[entry.slug]) {
        toUnblock.push(entry);
        _blockedScripts.splice(i, 1);
      }
    }
    toUnblock.forEach(function (entry) {
      const newScript = _origCreate('script');
      newScript.setAttribute('src', entry.src);
      const attrs = entry.node.attributes;
      for (let j = 0; j < attrs.length; j++) {
        const name = attrs[j].name;
        if (name === 'type' || name === 'src' || name.indexOf('data-cmp-') === 0) continue;
        newScript.setAttribute(name, attrs[j].value);
      }
      if (entry.node.parentNode) entry.node.parentNode.removeChild(entry.node);
      document.head.appendChild(newScript);
      console.log('CMP: unblocked script [' + entry.slug + ']', entry.src);
    });

    if (_observer) {
      _observer.disconnect();
      _observer = null;
    }
    startBlocking(choices);
  }

  // Aloita blokkaus heti (vain jos inline-blokkeria ei ole)
  if (!_hasInlineBlocker) {
    initBlockPatterns();
    const _storedForBlocking = storageGetStoredConsent(domain);
    _currentConsent = _storedForBlocking ? _storedForBlocking.choices : null;
    startBlocking(_currentConsent);
  }

  // ===== END SCRIPT BLOCKING =====

  const uiContext: BannerUiContext = {
    get config() { return CONFIG as CmpConfig; },
    lang: LANG,
    getStoredConsent: function () { return storageGetStoredConsent(domain); },
    getConsentId: function () { return getConsentId(domain); },
    storeConsent: function (choices, action) { storageStoreConsent(domain, choices, action); },
    applyConsent: applyConsent,
    sendConsent: function (choices, action, onDone) {
      apiSendConsent(API_BASE, domain, CONFIG as CmpConfig, LANG, choices, action, onDone);
    },
  };

  window.CMP_showBanner = function () {
    if (CONFIG) {
      const el = document.getElementById('cmp-overlay');
      if (el) el.remove();
      renderBanner(uiContext);
    }
  };

  // Init
  fetchBannerConfig(API_BASE, domain)
    .then(function (data) {
      if (data.error) { console.warn('CMP:', data.error); return; }
      CONFIG = data;
      // Säilytä GCM-lippu seuraavan sivulatauksen inline-blokkerille
      try {
        localStorage.setItem(gcmKey(domain), CONFIG.google_consent_mode_enabled === false ? '0' : '1');
      } catch { /* localStorage ei käytettävissä */ }
      // Tarkenna blokkauspatternit konfiguraation datalla
      refineBlockPatterns(CONFIG);
      // Tallenna blocking_patterns inline-blokkerin välimuistiin
      if (CONFIG.blocking_patterns) {
        try { localStorage.setItem(patternsKey(domain), JSON.stringify(CONFIG.blocking_patterns)); } catch { /* localStorage ei käytettävissä */ }
      }
      const stored = storageGetStoredConsent(domain);
      if (stored) {
        // Suostumus on jo annettu — sovella se (vapauta hyväksytyt kategoriat)
        applyConsent(stored.choices);
      } else {
        renderBanner(uiContext);
      }
    })
    .catch(function (e) { console.warn('CMP: failed to load config', e); });
}

export { showConfirmation };
